import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { intersection } from 'lodash/fp';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  GovernmentIdEnabledIdClassByCountries,
  VerificationTemplateVersion,
  VerificationTemplateVersionGovernmentId,
} from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import { ApiModel } from 'lib/interfaces/api';
import { CountryCodeLower, GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { CheckName } from 'lib/verificationCheck';

export const BARCODE_CHECK_MODAL_TITLE = 'Barcode collection required';
export const BARCODE_CHECK_MODAL_DESCRIPTION =
  'For this check to function correctly, ID types with barcodes are required.';

// displayed during auto-update path
export const BARCODE_MISSING_IDS_DESCRIPTION =
  'Currently, these ID types are enabled but require backside or barcode collection:';
export const BARCODE_AUTO_UPDATE_TEXT =
  'By clicking confirm barcode collection will automatically be enabled for the ID types above.';

// displayed during no barcode ids enabled path
export const BARCODE_NO_IDS_DESCRIPTION =
  'Currently, no ID types with barcodes are enabled, so the check will not run. Here are the ID types you can enable:';
export const BARCODE_NO_IDS_UPDATE_TEXT =
  'Please enable at least one of these ID types in the country configurations table below before enabling this check.';

export const BARCODE_DEPENDENT_CHECKS = [
  CheckName.IdBarcodeDetection,
  CheckName.IdBarcodeInconsistencyDetection,
  CheckName.IdExtractionInconsistencyDetection,
] as const;

export type BarcodeDependentCheckType = (typeof BARCODE_DEPENDENT_CHECKS)[number];

export interface BarcodeDependentCheck extends VerificationCheckConfig {
  check: BarcodeDependentCheckType;
}

export function isBarcodeDependentCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is BarcodeDependentCheck {
  switch (verificationCheck.check) {
    case CheckName.IdBarcodeDetection:
    case CheckName.IdBarcodeInconsistencyDetection:
    case CheckName.IdExtractionInconsistencyDetection:
      return true;
    default:
      return false;
  }
}

export function isGovIdVtmplVersion(
  vtmplVersion: VerificationTemplateVersion | undefined,
): vtmplVersion is VerificationTemplateVersionGovernmentId {
  switch (vtmplVersion?.type) {
    case ApiModel.VerificationTemplateVersionGovernmentId:
      return true;
    default:
      return false;
  }
}

const BARCODE_ID_COUNTRIES: Array<CountryCodeLower> = ['us', 'ca'];
const BARCODE_ID_CLASSES: Array<GovernmentIdClass> = ['dl', 'id'];
const BARCODE_COLLECTION_OPTIONS: Array<GovernmentIdSide> = [
  GovernmentIdSide.Back,
  GovernmentIdSide.BarcodePdf417,
];

export function hasBarcodeIdsEnabled(
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries,
): boolean {
  return (
    BARCODE_ID_COUNTRIES.flatMap((countryCode) => enabledIdClassesByCountry[countryCode])
      .filter(Boolean)
      .filter((idClassConfig) => BARCODE_ID_CLASSES.includes(idClassConfig.class)).length > 0
  );
}

type MisconfiguredBarcodeIds = Array<{ countryCode: CountryCodeLower; idClass: GovernmentIdClass }>;
const ALL_MISCONFIGURED_BARCODE_IDS: MisconfiguredBarcodeIds = [
  { countryCode: 'ca', idClass: 'dl' },
  { countryCode: 'ca', idClass: 'id' },
  { countryCode: 'us', idClass: 'dl' },
  { countryCode: 'us', idClass: 'id' },
];

export function getBarcodeIdsWithMissingCollection(
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries,
): MisconfiguredBarcodeIds {
  const misconfiguredIds: MisconfiguredBarcodeIds = [];
  BARCODE_ID_COUNTRIES.forEach((countryCode) => {
    (enabledIdClassesByCountry[countryCode] ?? []).forEach((idClassConfig) => {
      if (
        BARCODE_ID_CLASSES.includes(idClassConfig.class) &&
        intersection(idClassConfig['requires-sides'], BARCODE_COLLECTION_OPTIONS).length === 0
      ) {
        misconfiguredIds.push({ countryCode, idClass: idClassConfig.class });
      }
    });
  });
  return misconfiguredIds;
}

function getIdClassesWithBarcodeEnabled(
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries,
): GovernmentIdEnabledIdClassByCountries {
  return produce(enabledIdClassesByCountry, (draft) => {
    BARCODE_ID_COUNTRIES.forEach((countryCode) => {
      if (draft[countryCode] != null) {
        draft[countryCode]
          .filter((idClassConfig) => BARCODE_ID_CLASSES.includes(idClassConfig.class))
          .forEach((idClassConfig) => {
            idClassConfig['requires-sides'].push(GovernmentIdSide.BarcodePdf417);
          });
      }
    });
  });
}

type BarcodeCheckModalProps = {
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries;
  isOpen: boolean;
  handleClose: () => void;
  handleConfirm: (enabledIdClassesByCountry?: GovernmentIdEnabledIdClassByCountries) => void;
};

const BulletList = styled.ul`
  display: flex;
  flex-direction: column;
  grid-gap: ${Spacing[1]};
  padding: ${Spacing[4]} ${Spacing[7]};
`;

const BarcodeCheckModal = ({
  enabledIdClassesByCountry,
  isOpen,
  handleClose,
  handleConfirm,
}: BarcodeCheckModalProps) => {
  const hasEnabledBarcodeIds = hasBarcodeIdsEnabled(enabledIdClassesByCountry);
  const misConfiguredBarcodeIds = hasEnabledBarcodeIds
    ? getBarcodeIdsWithMissingCollection(enabledIdClassesByCountry)
    : ALL_MISCONFIGURED_BARCODE_IDS;

  const handleConfirmUpdate = () => {
    if (!hasEnabledBarcodeIds) {
      handleConfirm();
    } else {
      handleConfirm(getIdClassesWithBarcodeEnabled(enabledIdClassesByCountry));
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} handleCloseModal={handleClose}>
      <ModalContainer>
        <ModalHeader onClose={handleClose}>{BARCODE_CHECK_MODAL_TITLE}</ModalHeader>
        <ModalBody>
          <Paragraph>{BARCODE_CHECK_MODAL_DESCRIPTION}</Paragraph>
          <Spacer height={2} />
          <Paragraph>
            {hasEnabledBarcodeIds ? BARCODE_MISSING_IDS_DESCRIPTION : BARCODE_NO_IDS_DESCRIPTION}
          </Paragraph>
          <BulletList>
            {misConfiguredBarcodeIds.map(({ countryCode, idClass }) => {
              const idClassDisplay = idClass ? GOVERNMENT_ID_DISPLAY_STRINGS[idClass] : '';
              const countryDisplay = COUNTRIES[countryCode.toUpperCase() as CountryCode];
              return (
                <li key={`${countryCode}-${idClass}`}>
                  <Paragraph>{`${idClassDisplay} (${countryDisplay})`}</Paragraph>
                </li>
              );
            })}
          </BulletList>
          <Paragraph>
            {hasEnabledBarcodeIds ? BARCODE_AUTO_UPDATE_TEXT : BARCODE_NO_IDS_UPDATE_TEXT}
          </Paragraph>
        </ModalBody>
        <ModalFooter>
          <Button
            id='barcode-check-modal__cancel-button'
            variant='basic'
            aria-label='Cancel'
            onClick={handleClose}
          >
            Cancel
          </Button>
          {hasEnabledBarcodeIds && (
            <Button
              id='barcode-check-modal__confirm-button'
              variant='danger'
              aria-label='Confirm'
              onClick={handleConfirmUpdate}
            >
              Confirm
            </Button>
          )}
        </ModalFooter>
      </ModalContainer>
    </ModalWrapper>
  );
};

export default BarcodeCheckModal;
